// ts-rest contract for the AML Assessment (SEP-171).
//
// An `AML-####` record per assessment occasion, stacked on a Contact over time —
// the structural sibling of the Vulnerability Assessment contract. The Contact
// detail page needs two things:
//   - the summary the AML card shows ("AML Risk: Low / Medium / High", from the
//     most recently COMPLETED assessment),
//   - the stacked history rows (Score | Contact | Completed By | Completed
//     Date).
//
// Only the selected option values + the conditional high-risk occupation are
// stored; the per-question score, the total and the band are computed by the
// shared `computeAmlAssessment` helper (@septima/contract), which the web form
// recomputes live. The question table itself is imported directly from the
// package (`AML_QUESTIONS`, `AML_HIGH_RISK_OCCUPATIONS`); it is not an endpoint.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

import { errorBody } from '../schemas/common';

/**
 * `superseded` (SEP-179) — a record a later correction restates. It stays in
 * the history grid as evidence; every "current assessment" read filters on
 * `completed`.
 */
const statusSchema = z.enum(['draft', 'completed', 'superseded']);
const riskBandSchema = z.enum(['low', 'medium', 'high']);

/**
 * The stored answers: question key → the selected option `value` (`'yes'` /
 * `'no'`). Only answered questions are present.
 */
export const amlAnswersSchema = z.record(z.string(), z.string());
export type AmlAnswersDto = z.infer<typeof amlAnswersSchema>;

/** Controller-derived figures (mirror of `computeAmlAssessment`). */
export const amlAssessmentDerivedSchema = z.object({
    /** Sum of the selected options' scores. */
    score: z.number(),
    /** Count of questions with a recognised answer. */
    answered: z.number(),
    /** Number of questions on the form. */
    answerable: z.number(),
    /** Per-question contribution, keyed by question key — the form's score boxes. */
    questionScores: z.record(z.string(), z.number()),
    band: riskBandSchema,
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string(),
});

export const amlAssessmentDtoSchema = z.object({
    id: z.string(),
    /** AML-####. */
    reference: z.string().nullable(),
    contactId: z.string(),
    /** The Contact's display name — the history grid's "Contact" column. */
    contactName: z.string().nullable(),
    /** The owning IM (a `users` row id). */
    imUserId: z.string(),
    status: statusSchema,
    answers: amlAnswersSchema,
    /** The conditional cash-generative-business selection; null unless the
     * occupation question is answered `yes`. */
    highRiskOccupation: z.string().nullable(),
    derived: amlAssessmentDerivedSchema,
    /** Calendar completion date (`YYYY-MM-DD`) — null while draft. */
    completionDate: z.string().nullable(),
    createdAt: z.string(),
    /** Completion timestamp — null while draft. */
    completedAt: z.string().nullable(),
    /** The "Completed By" staff user id — null while draft. */
    completedByUserId: z.string().nullable(),
    /** Display name for `completedByUserId`, resolved server-side. */
    completedByName: z.string().nullable(),
    // --- Amendment chain (SEP-179) ---
    /** On a CORRECTION: the id of the assessment it restates. */
    supersedesId: z.string().nullable(),
    /** On a CORRECTION: that assessment's AML-####, resolved server-side. */
    supersedesReference: z.string().nullable(),
    /** On a SUPERSEDED record: the id of the correction that replaced it. */
    supersededById: z.string().nullable(),
    /** On a SUPERSEDED record: that correction's AML-####. */
    supersededByReference: z.string().nullable(),
    /** When the correction was recorded. */
    supersededAt: z.string().nullable(),
    /** Who recorded the correction. */
    supersededByUserId: z.string().nullable(),
    /** Display name for `supersededByUserId`, resolved server-side. */
    supersededByName: z.string().nullable(),
    /** On a CORRECTION: the free-text reason given. */
    amendmentReason: z.string().nullable(),
});
export type AmlAssessmentDto = z.infer<typeof amlAssessmentDtoSchema>;

/**
 * What the Contact's AML card shows: the risk band of the most recently
 * COMPLETED assessment. Every field is null when there is none.
 */
export const contactAmlSummarySchema = z.object({
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    band: riskBandSchema.nullable(),
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string().nullable(),
    score: z.number().nullable(),
    completionDate: z.string().nullable(),
});
export type ContactAmlSummary = z.infer<typeof contactAmlSummarySchema>;

/** The AML card summary + every assessment held against the Contact, newest first. */
export const listContactAmlAssessmentsResponseSchema = z.object({
    contactId: z.string(),
    contactName: z.string(),
    summary: contactAmlSummarySchema,
    assessments: z.array(amlAssessmentDtoSchema),
});
export type ListContactAmlAssessmentsResponse = z.infer<
    typeof listContactAmlAssessmentsResponseSchema
>;

/** Save body — the questionnaire's editable fields. Nothing derived is sent. */
const saveBodySchema = z.object({
    /** Absent → create a new draft; present → patch that draft. */
    id: z.string().optional(),
    answers: amlAnswersSchema,
    highRiskOccupation: z.string().nullable().optional(),
});

/**
 * Amend body (SEP-179) — the corrected content of a COMPLETED assessment.
 *
 * There is no `id` in the body: the record being corrected is the path
 * parameter, and the correction's own AML-#### is allocated server-side. The
 * assessment occasion (completion date, completed-by) is inherited from the
 * original and is deliberately not editable here — this endpoint fixes what was
 * recorded, not when it happened or who did it.
 */
const amendBodySchema = z.object({
    answers: amlAnswersSchema,
    highRiskOccupation: z.string().nullable().optional(),
    /** Why the record is being corrected. Optional free text. */
    amendmentReason: z.string().nullable().optional(),
});

/** Complete body — the "Completed By" decision (defaults to the caller). */
const completeBodySchema = z
    .object({
        completedByUserId: z.string().optional(),
    })
    .optional();

export const amlAssessmentContract = c.router(
    {
        listContactAmlAssessments: {
            method: 'GET',
            path: '/contacts/:contactId/aml-assessments',
            pathParams: z.object({ contactId: z.string() }),
            responses: {
                200: listContactAmlAssessmentsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A Contact's AML card summary + assessment history (newest first)",
        },
        saveContactAmlAssessment: {
            method: 'POST',
            path: '/contacts/:contactId/aml-assessments/save',
            pathParams: z.object({ contactId: z.string() }),
            body: saveBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: "Create or patch a Contact's draft AML assessment",
        },
        completeAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: completeBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Complete an AML assessment (draft → completed)',
        },
        amendAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/amend',
            pathParams: z.object({ id: z.string() }),
            body: amendBodySchema,
            responses: {
                // The CORRECTION, not the record that was corrected.
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Correct a completed AML assessment (supersedes the original)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
